import { environment } from '@env/environment';

/**
 * Shared, MUTABLE `protectedResourceMap` singleton — deliberately module-level, not created
 * fresh inside `MSALInterceptorConfigFactory()` (in `auth.service.ts`).
 *
 * Deliberately extracted into its OWN, dependency-free file (2026-09-28): `auth.service.ts` needs
 * to read `AppConfig.instance?.backendUrl` (best-effort, at DI-construction time), and
 * `app-config.ts` needs to call `updateProtectedResourceMap()` once the real `backendUrl` is
 * known — putting both in `auth.service.ts` created a genuine circular import
 * (`app-config.ts` -> `auth.service.ts` -> `app-config.ts`). `app-config.ts` is used extremely
 * early (constructor-injected into `APP_INITIALIZER`), so that cycle transitively pulled
 * `@azure/msal-browser`/`@azure/msal-angular` into the module-evaluation graph at a much earlier
 * point than before — a needless risk given the NG0204 class of bootstrap-ordering bugs already
 * hit once in this migration (see `TODO-NEXT.md` items 11/12). This neutral file has ZERO
 * dependency on either `auth.service.ts` or `app-config.ts`, so both can safely import it as a
 * one-way dependency with no cycle at all.
 *
 * `MsalInterceptor` dereferences `this.msalInterceptorConfig.protectedResourceMap` live, on every
 * single request (see `matchResourceMapValue`/`matchScopesToEndpoint` in
 * `@azure/msal-angular/fesm2015/azure-msal-angular.js`) — but the `MSAL_INTERCEPTOR_CONFIG`
 * provider itself (and therefore the whole config object returned by `MSALInterceptorConfigFactory`)
 * is only ever constructed ONCE, lazily, the first time `HTTP_INTERCEPTORS` needs to be resolved
 * by Angular's DI. Root-caused 2026-09-28 (`TODO-NEXT.md` item 12): that very first HTTP request in
 * this app is `AppConfig.load()`'s own call to fetch `config.dev.json`/`config.json` — at that
 * precise moment `AppConfig.instance` is BY DEFINITION still `undefined` (this is the request that
 * populates it). A factory that reads `AppConfig.instance?.backendUrl` synchronously therefore
 * always built an EMPTY map, and — because DI providers are singletons — that empty map stayed
 * empty forever: `MsalInterceptor` silently never attached a Bearer token to ANY request for the
 * rest of the app's life.
 *
 * Fix: keep handing `MsalInterceptor` the SAME `Map` instance, and only ever mutate its entries in
 * place via `updateProtectedResourceMap()`, called again from `AppConfig.setConfigFile()` once the
 * real `backendUrl` is actually known. Because `Map` is a reference type and `MsalInterceptor`
 * reads it live (not a snapshot taken at construction time), it will see the freshest content on
 * every subsequent request without needing the `MSAL_INTERCEPTOR_CONFIG` provider itself to be
 * re-created.
 */
export const protectedResourceMap = new Map<string, Array<string> | null>();

/**
 * (Re)populates the shared `protectedResourceMap` singleton above. Must be called every time the
 * real `backendUrl` becomes known — currently from `AppConfig.setConfigFile()` — in addition to
 * the best-effort call made inside `MSALInterceptorConfigFactory()` (which will still build an
 * empty map on the very first bootstrap, before config is loaded, see comment above).
 *
 * IMPORTANT: keys use `<origin>/api/*` and `<origin>/ui/*` (NOT a blanket `<origin>/*`)
 * because in the proxy dev config (`environment.proxy.ts`), `backendUrl` resolves to
 * the app's own origin (`https://localhost:4200`) — a bare `/*` wildcard would then
 * match every request the app makes (static assets, i18n files, etc.), which can break
 * unrelated calls before `MsalGuard` has had a chance to initialize the MSAL instance.
 */
export function updateProtectedResourceMap(backendUrl: string | undefined): void {
	protectedResourceMap.clear();
	if (backendUrl) {
		protectedResourceMap.set(`${backendUrl}/api/*`, environment.apiConfig.scopes);
		protectedResourceMap.set(`${backendUrl}/ui/*`, environment.apiConfig.scopes);
	}
}

